import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';
import { useOrganization } from '@/hooks/useOrganization';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useInvoice } from '@/hooks/useInvoice';
import { useStagedProductsStore } from '@/stores/stagedProductsStore';
import { useDispatchReprintsStore } from '@/stores/dispatchReprintsStore';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import {
  offlineMutationKeys,
  executeAddItemsToTab,
  applyAddItemsOptimistically,
  type AddItemsToTabVariables,
} from '@/lib/offlineMutations';
import { generateUuid } from '@/lib/uuid';
import { lineUnitPrice, toTabItemModifiers } from '@/lib/productModifiers';
import { queryKeys } from '@/lib/queryKeys';
import { resolvePromotionComponentsForDispatch } from '@/services/promotions/promotionDispatch';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import type { DispatchPrintTab } from '@/types/invoice';
import type { ProductPromotion, TabItem } from '@/types';
import { calculateWeightLinePrice, hasPositiveWeight } from '@/utils/weightPricing';

export interface CommitStagedProductsParams {
  tabId: string;
  customerName: string;
  tableId?: string | null;
  serverName?: string;
  onComplete?: () => void;
  getPromotionForProduct?: (productId: string) => ProductPromotion | null | undefined;
  productNameById?: Map<string, string>;
}

/**
 * Commits the staged cart to a tab through the offline-capable add-items
 * mutation: the cache updates optimistically, the server write pauses/replays
 * when offline, and the kitchen ticket prints in the background so a dead
 * printer cannot block adding items to the tab.
 *
 * Replaces the old `commitStagedProducts` store action, which called the service
 * directly and therefore failed outright without a connection.
 */
export const useCommitStagedProducts = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { generateDispatchOrder } = useInvoice();
  const { translator } = useContext(LanguageProviderContext);

  const tabsKey = queryKeys.tabs(currentOrganization?.id ?? '');
  const productsKey = queryKeys.products(currentOrganization?.id ?? '');
  const ingredientsKey = queryKeys.ingredients(currentOrganization?.id ?? '');

  const { isEnabled } = useFeatureFlag();
  const createKitchenTickets = isEnabled(FEATURE_FLAGS.KITCHEN_VIEW);
  const customerPositionsEnabled = isEnabled(FEATURE_FLAGS.CUSTOMER_POSITIONS);

  const addItemsMutation = useMutation({
    mutationKey: offlineMutationKeys.addItemsToTab,
    mutationFn: (variables: AddItemsToTabVariables) => executeAddItemsToTab(variables),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tabsKey });
      await queryClient.cancelQueries({ queryKey: productsKey });
      await queryClient.cancelQueries({ queryKey: ingredientsKey });
      await queryClient.cancelQueries({ queryKey: queryKeys.externalOrders(currentOrganization?.id ?? '') });

      return applyAddItemsOptimistically(queryClient, variables);
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTabs) {
        queryClient.setQueryData(tabsKey, context.previousTabs);
      }
      if (context?.previousProducts) {
        queryClient.setQueryData(productsKey, context.previousProducts);
      }
      if (context?.previousExternalOrders) {
        queryClient.setQueryData(
          queryKeys.externalOrders(currentOrganization?.id ?? ''),
          context.previousExternalOrders,
        );
      }

      sonnerToast.error(translator('Errors.errorProduct'), {
        description: translator('Errors.errorProductDesc'),
        duration: 3000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey });
      queryClient.invalidateQueries({ queryKey: productsKey });
      queryClient.invalidateQueries({ queryKey: ingredientsKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.externalOrders(currentOrganization?.id ?? '') });
    },
  });

  const commitStagedProducts = async ({
    tabId,
    customerName,
    tableId,
    serverName,
    onComplete,
    getPromotionForProduct,
    productNameById,
  }: CommitStagedProductsParams): Promise<void> => {
    const store = useStagedProductsStore.getState();
    if (!store.stagedProducts.length || store.isCommitting) {
      return;
    }

    store.setIsCommitting(true);
    store.setEditingNotesId(null);

    try {
      const items = store.stagedProducts.map(({ product, quantity, notes, customerPosition, promotionSelections, modifiers, weightKg }) => ({
        itemId: generateUuid(),
        product,
        quantity: weightKg && weightKg > 0 ? 1 : quantity,
        notes: notes?.trim() || undefined,
        customerPosition: customerPositionsEnabled ? customerPosition : undefined,
        promotionSelections,
        // Weight lines are priced per kg, so a flat modifier price never applies to them.
        modifiers: modifiers?.length && !(weightKg && weightKg > 0) ? modifiers : undefined,
        weightKg: weightKg && weightKg > 0 ? weightKg : undefined,
      }));

      const dispatchItems: TabItem[] = items.map((entry) => {
        const definition = getPromotionForProduct?.(entry.product.id);
        const promotionComponents =
          definition && productNameById
            ? resolvePromotionComponentsForDispatch(
                definition,
                entry.promotionSelections,
                productNameById
              )
            : undefined;

        const hasWeight = hasPositiveWeight(entry.weightKg);

        return {
          id: entry.itemId,
          productId: entry.product.id,
          product: entry.product,
          quantity: entry.quantity,
          unitPrice: hasWeight
            ? calculateWeightLinePrice(entry.product.price, entry.weightKg)
            : lineUnitPrice(entry.product, entry.modifiers),
          discount: 0,
          notes: entry.notes,
          customerPosition: entry.customerPosition,
          weightKg: hasWeight ? entry.weightKg : undefined,
          promotionComponents: promotionComponents?.length ? promotionComponents : undefined,
          modifiers: toTabItemModifiers(entry.itemId, entry.modifiers),
        };
      });

      const dispatchPayload: DispatchPrintTab | undefined =
        dispatchItems.length > 0
          ? {
              id: tabId,
              customerName,
              items: dispatchItems,
              createdAt: new Date(),
              tableId,
              serverName,
            }
          : undefined;

      // Fire-and-forget: the optimistic update lands synchronously; offline the
      // mutation pauses and replays on reconnect. The reprint snapshot travels
      // with the mutation so it still persists after the catalog unmounts.
      addItemsMutation.mutate({
        tabId,
        organizationId: currentOrganization?.id ?? '',
        items,
        createKitchenTickets,
        dispatchReprint: dispatchPayload,
      });

      // Local cache immediately so reprint works offline before the mutation syncs.
      if (dispatchPayload && currentOrganization?.id) {
        useDispatchReprintsStore.getState().addRecord(
          currentOrganization.id,
          tabId,
          dispatchPayload,
        );
      }

      store.clearStagedProducts();
      onComplete?.();

      // Print after the tab write is queued. A TCP timeout to a powered-off
      // printer must not keep isCommitting true or delay adding items.
      if (dispatchPayload) {
        void generateDispatchOrder(dispatchPayload);
      }

    } catch (error) {
      console.error('Failed to commit staged products:', error);
      throw error;
    } finally {
      store.setIsCommitting(false);
    }
  };

  return { commitStagedProducts };
};
